import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { supabase } from '../../shared/supabase/supabaseClient';

/**
 * Suscripción Realtime a appointments de la sucursal actual.
 * Solo mientras la pantalla tiene foco y la app está visible.
 * Llama a onChanged (silent reload) cuando hay inserts/updates/deletes.
 */
export function useAppointmentsRealtime(
  orgId: string | null,
  onChanged: () => void,
  branchId?: string | null,
  /** Known resource ids — narrows the time-off listener (that table has no branch_id). */
  resourceIds?: readonly string[],
) {
  const [focused, setFocused] = useState(false);
  const resourceIdsKey =
    resourceIds == null ? null : resourceIds.length > 0 ? resourceIds.join(',') : 'none';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(true);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  useEffect(() => {
    if (!orgId || !focused || !branchId) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChangedRef.current();
      }, 500);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const channelName = `appointments-realtime-${orgId}-${branchId}`;

    const subscribe = () => {
      if (cancelled || !orgId || !branchId) return;
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments',
            filter: `branch_id=eq.${branchId}`,
          },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointment_resources',
            filter: `branch_id=eq.${branchId}`,
          },
          scheduleRefresh,
        )
        // appointment_resource_time_off has no branch_id column — filter by the
        // known resource ids instead. resourceIds omitted (still loading) →
        // catch-all like before; empty array → skip (no resources, no time-offs).
        .on(
          'postgres_changes',
          resourceIdsKey == null
            ? {
                event: '*',
                schema: 'public',
                table: 'appointment_resource_time_off',
              }
            : {
                event: '*',
                schema: 'public',
                table: 'appointment_resource_time_off',
                filter:
                  resourceIdsKey === 'none'
                    ? 'resource_id=eq.00000000-0000-0000-0000-000000000000'
                    : `resource_id=in.(${resourceIdsKey})`,
              },
          scheduleRefresh,
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
            // Reconnect with backoff
            if (channel) void supabase.removeChannel(channel);
            channel = null;
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => subscribe(), 3000);
          }
        });
    };

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;
      subscribe();
    })();

    const appSub = AppState.addEventListener('change', (state) => {
      visibleRef.current = state === 'active';
      if (state === 'active') scheduleRefresh();
      if (state !== 'active' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) scheduleRefresh();
        if (!visibleRef.current && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      visibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (retryTimer) clearTimeout(retryTimer);
      if (channel) void supabase.removeChannel(channel);
      appSub.remove();
      removeVisibility?.();
    };
  }, [orgId, branchId, focused, resourceIdsKey]);
}
